import type { PersonRef } from '../domain/people';
import { avatarColorClasses } from '../utils/avatarColors';
import { fullName, initials } from '../utils/people';

interface UserAvatarProps {
  person: Pick<PersonRef, 'firstName' | 'lastName' | 'avatarUrl'>;
  /** Tailwind size classes for the circle, e.g. `size-8`. */
  className?: string;
  /** Tailwind text-size class for the initials fallback. */
  textClassName?: string;
  /**
   * Give the photo the person's name as its accessible name. Off by default:
   * most call sites print the name right beside it, and a screen reader
   * shouldn't hear it twice.
   */
  labelled?: boolean;
}

/** A person's photo, or their initials on their own colour when they have none. */
export function UserAvatar({
  person,
  className = 'size-8',
  textClassName = 'text-sm',
  labelled = false,
}: UserAvatarProps) {
  if (person.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt={labelled ? fullName(person) : ''}
        className={`${className} shrink-0 rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`flex ${className} shrink-0 items-center justify-center rounded-full ${avatarColorClasses(fullName(person))}`}
      aria-hidden="true"
    >
      <span className={`${textClassName} font-semibold`}>{initials(person)}</span>
    </div>
  );
}

export default UserAvatar;
